import { IEmployee, IEmployeeRole, SystemError } from '@posx/core';
import { defineStore } from 'pinia';
import { markRaw } from 'vue';
import { Subscription } from 'dexie';
import { useEmployeeRoleStore, getExpectedPermissions } from './employee.role';
import { useEmployeeStore } from './employee';
import { eventBus } from '@/libs/EventBus';
import secureStorage from '@xpos/utils/src/secure.storage.utils';
import { logLiveQueryError, isLiveQueryCursorError, logInfo } from '@/utils/logger';
import { notify } from '@/utils/notification';
import { cleanupSessionData } from '@/utils/cleanup';
import { i18n } from '@/main';
import router from '@/routes';

// Compare two permission arrays (order-insensitive)
const isSamePermissions = (a: string[], b: string[]) => a.length === b.length && a.every(p => b.includes(p));

export const useAcl = defineStore('acl', {
  state: () => ({
    currentEmployeeRole: null as IEmployeeRole | null,
    _subscription: null as Subscription | null,
    _employeeSubscription: null as Subscription | null,
  }),
  actions: {
    async setEmployeeRole(employee: IEmployee) {
      if (!employee) return;
      const employeeRoleStore = useEmployeeRoleStore();
      const employeeRoleService = await employeeRoleStore.getService();
      const employeeStore = useEmployeeStore();
      const employeeService = await employeeStore.getService();
      const defaultRole = await employeeRoleStore.service?.getOneByParams({ is_default: true });
      if (!defaultRole) throw new SystemError('Default role not found');
      // Get current employee's role
      let role = employee.role_uid ? await employeeRoleService.getOne(employee.role_uid) : null;
      if (!role) role = defaultRole;
      // Auto-update permissions only for default roles (custom roles managed by admin)
      if (['Cashier', 'Manager', 'Admin'].includes(role.name)) {
        const expectedPermissions = getExpectedPermissions(role.name);
        if (!isSamePermissions(role.permissions, expectedPermissions)) {
          role.permissions = expectedPermissions;
          await employeeRoleService.updateOne(role.id_in_server, role);
        }
      }
      this.currentEmployeeRole = role;
      secureStorage.setItem('currentEmployeeRole', JSON.stringify(role));
      // Keep the role reactive so permission edits made on another device apply without re-login; a shrunk permission just fails the next check, no forced kick-out
      this.stopEmployeeRoleLiveQuery();
      const sub: Subscription = employeeRoleService.liveQuery(() => employeeRoleService.getOne(role.uid)).subscribe({
        next: (updated) => {
          if (!updated) return;
          this.currentEmployeeRole = updated;
          secureStorage.setItem('currentEmployeeRole', JSON.stringify(updated));
        },
        error: (err) => { logLiveQueryError(err, { store: 'acl', uid: role.uid }); if (isLiveQueryCursorError(err) && this._subscription === sub) { sub.unsubscribe(); this._subscription = null; } },
      });
      this._subscription = markRaw(sub);
      // Auto logout when another device disables or deletes the current employee; a missing row is skipped as it can be a transient resync state
      const staffSub: Subscription = employeeService.liveQuery(() => employeeService.getOne(employee.uid)).subscribe({
        next: async (row) => {
          const staff = await row;
          if (!staff || (staff.can_login !== false && !staff.deleted_at)) return;
          this.stopEmployeeRoleLiveQuery(); // stop before awaiting logout so a repeated emission cannot log out twice
          logInfo('Auto logout disabled employee', { uid: employee.uid, reason: staff.deleted_at ? 'deleted' : 'disabled' });
          await employeeStore.logout();
          await cleanupSessionData();
          router.push('/login');
          notify.info(i18n.global.t('account.disabledLoggedOut'));
        },
        error: (err) => { logLiveQueryError(err, { store: 'acl', uid: employee.uid }); if (isLiveQueryCursorError(err) && this._employeeSubscription === staffSub) { staffSub.unsubscribe(); this._employeeSubscription = null; } },
      });
      this._employeeSubscription = markRaw(staffSub);
    },
    stopEmployeeRoleLiveQuery() {
      this._subscription?.unsubscribe();
      this._employeeSubscription?.unsubscribe();
      this._subscription = this._employeeSubscription = null;
    },
    getCurrentEmployeeRole(): IEmployeeRole | null {
      if(this.currentEmployeeRole) return this.currentEmployeeRole;
      const role = secureStorage.getItem('currentEmployeeRole');
      this.currentEmployeeRole = role ? JSON.parse(role) : null;
      return this.currentEmployeeRole;
    },
    // Pure permission check, no side effects — safe for computed disabled states
    checkPermission(permission: string): boolean {
      return this.getCurrentEmployeeRole()?.permissions.includes(permission) ?? false;
    },
    // Permission check that emits 'permission denied' event on failure — for click handlers
    requirePermission(permission: string): boolean {
      const allowed = this.checkPermission(permission);
      if (!allowed) eventBus.emit('permission denied');
      return allowed;
    },
    hasPermission(permission: string): boolean {
      return this.requirePermission(permission);
    },
  },
});
